import { FormEvent, useMemo, useState } from "react";
import { Mail, MessageCircle, Send } from "lucide-react";
import { AnimatedPage } from "../components/AnimatedPage";
import { PageHero } from "../components/PageHero";
import { Section } from "../components/Section";
import {
  contactMethods,
  officialEmail,
  officialPhone,
  officialWhatsAppUrl,
  socialLinks,
} from "../data/site";

const projectTypes = [
  "Website Development",
  "Mobile App",
  "SaaS Platform",
  "E-commerce",
  "VTU Platform",
  "Enterprise Software",
  "Custom Business App",
];

const budgetRanges = [
  "Custom Quote",
  "Below NGN 500k",
  "NGN 500k - NGN 2m",
  "NGN 2m - NGN 5m",
  "NGN 5m+",
  "Enterprise",
];

export default function Contact() {
  const [status, setStatus] = useState("");
  const [whatsAppLink, setWhatsAppLink] = useState(officialWhatsAppUrl);

  const defaultWhatsAppLink = useMemo(
    () =>
      `${officialWhatsAppUrl}?text=${encodeURIComponent(
        "Hello Lazerwebs, I want to start a project.",
      )}`,
    [],
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const data = new FormData(event.currentTarget);
    const inquiry = {
      name: String(data.get("name") || "").trim(),
      email: String(data.get("email") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      company: String(data.get("company") || "").trim(),
      projectType: String(data.get("projectType") || "").trim(),
      budget: String(data.get("budget") || "").trim(),
      message: String(data.get("message") || "").trim(),
    };

    const body = [
      "New Lazerwebs project request",
      "",
      `Full name: ${inquiry.name}`,
      `Email: ${inquiry.email}`,
      `Phone: ${inquiry.phone}`,
      `Company: ${inquiry.company || "Not provided"}`,
      `Project type: ${inquiry.projectType}`,
      `Budget range: ${inquiry.budget}`,
      "",
      "Message:",
      inquiry.message,
    ].join("\n");

    const subject = `Project request from ${
      inquiry.name || "Lazerwebs website"
    }`;
    const mailto = `mailto:${officialEmail}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    const whatsapp = `${officialWhatsAppUrl}?text=${encodeURIComponent(body)}`;

    setWhatsAppLink(whatsapp);
    setStatus(
      "Your email app should open with the project brief. You can also continue on WhatsApp with the same details.",
    );
    window.location.href = mailto;
  }

  return (
    <AnimatedPage>
      <PageHero
        eyebrow="Contact"
        title="Tell us what you want to build"
        description="Share your goals, timeline, and project type. Lazerwebs will help shape the right digital solution for your business."
      />
      <Section align="left">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <form
            onSubmit={handleSubmit}
            className="glass-card p-6 sm:p-8"
            aria-label="Project inquiry form"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label="Full name"
                name="name"
                placeholder="Your full name"
                required
              />
              <Field
                label="Email address"
                name="email"
                type="email"
                placeholder="you@company.com"
                required
              />
              <Field
                label="Phone number"
                name="phone"
                type="tel"
                placeholder={officialPhone}
                required
              />
              <Field
                label="Company name"
                name="company"
                placeholder="Company or organization"
              />
              <Select
                label="Project type"
                name="projectType"
                options={projectTypes}
              />
              <Select
                label="Budget range"
                name="budget"
                options={budgetRanges}
              />
            </div>
            <label className="mt-5 block">
              <span className="form-label">Message</span>
              <textarea
                name="message"
                rows={6}
                placeholder="Tell us about the product, features, audience, timeline, and goals."
                className="form-input resize-none"
                required
              />
            </label>
            <button
              type="submit"
              className="btn btn-primary mt-6 w-full justify-center"
            >
              Submit Project Request
              <Send className="h-4 w-4" />
            </button>
            {status && (
              <div className="mt-5 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm leading-6 text-emerald-100">
                <p>{status}</p>
                <a
                  href={whatsAppLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex font-semibold text-white underline decoration-emerald-200/40 underline-offset-4"
                >
                  Continue on WhatsApp
                </a>
              </div>
            )}
          </form>
          <aside className="grid gap-5">
            <div className="glass-card p-6">
              <h2 className="font-display text-2xl font-semibold text-white">
                Direct contact
              </h2>
              <p className="mt-3 text-sm leading-6 text-white/60">
                Use WhatsApp, phone, or email for demo requests, project
                scoping, and support discussions.
              </p>
              <div className="mt-6 grid gap-3">
                <a
                  href={defaultWhatsAppLink}
                  target="_blank"
                  rel="noreferrer"
                  className="contact-tile"
                >
                  <MessageCircle className="h-5 w-5 text-emerald-200" />
                  <span>
                    <span className="block font-semibold text-white">
                      WhatsApp
                    </span>
                    <span className="text-sm text-white/55">
                      {officialPhone}
                    </span>
                  </span>
                </a>
                <a href={`mailto:${officialEmail}`} className="contact-tile">
                  <Mail className="h-5 w-5 text-sky-200" />
                  <span>
                    <span className="block font-semibold text-white">Email</span>
                    <span className="text-sm text-white/55">
                      {officialEmail}
                    </span>
                  </span>
                </a>
              </div>
            </div>
            <div className="glass-card p-6">
              <h2 className="font-display text-2xl font-semibold text-white">
                Office and location
              </h2>
              <div className="mt-5 grid gap-4">
                {contactMethods.map((method) => {
                  const Icon = method.icon;
                  const isExternal = method.href.startsWith("http");
                  return (
                    <a
                      key={method.label}
                      href={method.href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noreferrer" : undefined}
                      className="contact-tile"
                    >
                      <Icon className="h-5 w-5 text-electric" />
                      <span>
                        <span className="block font-semibold text-white">
                          {method.label}
                        </span>
                        <span className="text-sm text-white/55">
                          {method.value}
                        </span>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
            <div className="glass-card p-6">
              <h2 className="font-display text-2xl font-semibold text-white">
                Social links
              </h2>
              <div className="mt-5 flex flex-wrap gap-2">
                {socialLinks.map((link) => (
                  <a
                    href="#"
                    key={link}
                    className="rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-white/60 transition hover:border-electric/50 hover:text-white"
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </AnimatedPage>
  );
}

function Field({
  label,
  name,
  placeholder,
  type = "text",
  required = false,
}: {
  label: string;
  name: string;
  placeholder: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="form-label">{label}</span>
      <input
        className="form-input"
        type={type}
        name={name}
        placeholder={placeholder}
        required={required}
      />
    </label>
  );
}

function Select({
  label,
  name,
  options,
}: {
  label: string;
  name: string;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="form-label">{label}</span>
      <select name={name} className="form-input" required>
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}
